import { API_PREFIX, type ApiResponse, type ErrorCode } from 'shared';

/** error ที่มาจาก API พร้อม code ที่ frontend ใช้ตัดสินใจได้ (เช่น ATTEMPT_EXPIRED → เด้งไปหน้าส่ง) */
export class ApiError extends Error {
  public readonly code: ErrorCode | 'NETWORK_ERROR';
  public readonly status: number;
  public readonly details?: unknown;

  constructor(code: ErrorCode | 'NETWORK_ERROR', message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  sessionToken?: string;
  signal?: AbortSignal;
};

/**
 * fetch wrapper — คลี่ envelope { ok, data } ให้อัตโนมัติ และโยน ApiError เมื่อไม่สำเร็จ
 * path ใส่แบบไม่มี prefix เช่น '/health' หรือ '/public/exam/abc'
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, sessionToken, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (sessionToken) headers['X-Session-Token'] = sessionToken;

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    throw new ApiError('NETWORK_ERROR', 'เชื่อมต่อเครือข่ายไม่ได้', 0, error);
  }

  let payload: ApiResponse<T> | null = null;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    payload = null;
  }

  if (!payload) {
    throw new ApiError('NETWORK_ERROR', 'เซิร์ฟเวอร์ตอบกลับในรูปแบบที่ไม่รู้จัก', response.status);
  }

  if (!payload.ok) {
    throw new ApiError(payload.error.code, payload.error.message, response.status, payload.error.details);
  }

  return payload.data;
}
